// The hub's carousel, drawn as the extension's is (extension/ui/browse.html:
// `.carousel`, `.cardwrap`, `vinylHtml`, `.glass`): one seed per snap slide,
// a glass card with its record sliding out from behind it, the neighbours
// shrunk and dimmed. Same sizes, same label palette and hash, same curved
// label text — a seed looks the same in Live and here. Only the footer
// buttons are taller than the extension's: they're thumb targets on a phone.
//
// On a phone the whole card-and-record unit scales down to fit, so it reads
// exactly as it does in Live, only smaller.

import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, Loader2, Maximize2, Plus } from "lucide-react";
import { registerExternalPreview } from "@web-shared/lib/external-previews";
import { cn } from "@web-shared/lib/utils";
import { initials, labelColor, labelText } from "@/lib/cover";

/** One seed as a hub card shows it, whichever list it came from. */
export interface HubSeed {
  id: string;
  title: string;
  bpm: number;
  key: string | null;
  genre: string | null;
  who: string;
  /** Under the name: this week's Flip, how far it has travelled, or when. */
  badge: { text: string; tone: "flip" | "remix" | "time" };
  audioUrl: string | null;
}

/** What a card's buttons do — the modal decides where each one goes. */
export interface CardActions {
  /** The primary: Remix (someone's) or Open (yours). */
  primary: { label: string; onClick: (seed: HubSeed) => void };
  /** Absent where the primary already is the jam (Start a jam › Get inspired). */
  onJam?: (seed: HubSeed) => void;
  onDetails: (seed: HubSeed) => void;
  /** About to preview a card: the page silences the Flip. */
  onPreview: () => void;
}

/** The hub's ⤢ (open the full page), handed down by the modal: on a phone it
 *  sits at the end of the panel's top row, not in the header, so the header
 *  fits on one line. Null where there's no full page to open. */
export const HubExpand = createContext<(() => void) | null>(null);

export function ExpandButton() {
  const expand = useContext(HubExpand);
  if (!expand) return null;
  return (
    <button
      type="button"
      onClick={expand}
      aria-label="Open the full page"
      title="Open the full page"
      className="flex size-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-foreground/70 hover:bg-white/5 sm:hidden"
    >
      <Maximize2 className="size-4" />
    </button>
  );
}

export function Carousel({
  seeds,
  pending,
  actions,
  onUpload,
  controls,
  above,
  empty,
}: {
  seeds: HubSeed[];
  pending: boolean;
  actions: CardActions;
  /** Absent in the first-visit tutorial views: one way forward per card. */
  onUpload?: () => void;
  controls: ReactNode;
  above?: ReactNode;
  empty: ReactNode;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(0);
  const [zoom, setZoom] = useState(1);
  const hasSeeds = !pending && seeds.length > 0;
  useEffect(() => {
    const el = track.current;
    if (!el) return;
    const fit = () => setZoom(Math.min(1, el.clientWidth / UNIT_W));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasSeeds]);

  // The most-centred slide is the active one, as the extension's
  // updateActiveSlide decides it.
  const onScroll = () => {
    const el = track.current;
    if (!el || el.clientWidth === 0) return;
    setAt(Math.max(0, Math.min(seeds.length - 1, Math.round(el.scrollLeft / el.clientWidth))));
  };
  const step = (d: number) => track.current?.scrollBy({ left: d * track.current.clientWidth, behavior: "smooth" });

  return (
    <div className="flex min-h-[26rem] flex-col">
      <div className="mb-3 flex items-center gap-2">
        {/* A phone swipes; the arrows are for a pointer. */}
        <div className="hidden gap-1.5 sm:flex">
          <RoundButton label="Previous seed" disabled={at === 0} onClick={() => step(-1)}>
            <ChevronLeft className="size-4" />
          </RoundButton>
          <RoundButton label="Next seed" disabled={at >= seeds.length - 1} onClick={() => step(1)}>
            <ChevronRight className="size-4" />
          </RoundButton>
        </div>
        {controls}
        <ExpandButton />
      </div>
      {above && <div className="mb-3">{above}</div>}
      {pending ? (
        <div className="flex flex-1 items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : seeds.length === 0 ? (
        <div className="flex flex-1 items-center">
          <div className="w-full">{empty}</div>
        </div>
      ) : (
        <div
          ref={track}
          onScroll={onScroll}
          className="flex flex-1 snap-x snap-mandatory items-center overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {seeds.map((seed, n) => (
            // The extension's slide: room below for the card's 60px shadow (the
            // track clips it), and the record's overhang on the right.
            <div key={seed.id} className="flex w-full shrink-0 snap-center justify-center pb-16 pt-2.5">
              <div style={{ zoom }} className="pr-[62px]">
                <SeedCardView seed={seed} active={n === at} actions={actions} />
              </div>
            </div>
          ))}
        </div>
      )}
      <div className={cn("flex min-h-12 items-center gap-3", hasSeeds ? "-mt-9" : "mt-3", !onUpload && "justify-center")}>
        {seeds.length > 1 && (
          <>
            <div className="flex gap-1">
              {seeds.slice(0, 8).map((s, n) => (
                <span key={s.id} className={cn("h-1.5 rounded-full transition-all", n === at ? "w-5 bg-primary" : "w-1.5 bg-white/20")} />
              ))}
            </div>
            <span className="font-mono text-xs text-muted-foreground">
              {at + 1} / {seeds.length}
            </span>
          </>
        )}
        {onUpload && (
          <button
            type="button"
            onClick={onUpload}
            // Ghost: the card's Remix is this screen's one mint button.
            className="ml-auto flex h-11 items-center gap-2 rounded-full border border-white/15 px-5 text-sm font-bold transition-colors hover:border-white/30 hover:bg-white/5"
          >
            <Plus className="size-4" /> Plant
          </button>
        )}
      </div>
    </div>
  );
}

/** The card (320) plus the record's overhang (36 + 26 slid out). */
const UNIT_W = 382;

const BADGE_TONE: Record<HubSeed["badge"]["tone"], string> = {
  flip: "text-[#4bcc88]",
  remix: "text-[#f0b429]",
  time: "text-[#8a8a8e]",
};

function SeedCardView({ seed, active, actions }: { seed: HubSeed; active: boolean; actions: CardActions }) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  // Registered with the room's Stop while it plays, so the Flip's ▶ (or a
  // Space press) silences it, as it does every other preview.
  const unregister = useRef<(() => void) | null>(null);
  const halt = () => {
    audio.current?.pause();
    unregister.current?.();
    unregister.current = null;
    setPlaying(false);
  };
  const haltRef = useRef(halt);
  haltRef.current = halt;
  useEffect(() => () => haltRef.current(), []);
  // Swiped away: it stops, as the extension's carousel stops on a re-render.
  useEffect(() => {
    if (!active) haltRef.current();
  }, [active]);

  const toggle = () => {
    if (!seed.audioUrl) return;
    if (!audio.current) {
      audio.current = new Audio(seed.audioUrl);
      audio.current.onended = () => haltRef.current();
    }
    if (playing) {
      halt();
    } else {
      actions.onPreview();
      unregister.current = registerExternalPreview(() => haltRef.current());
      void audio.current.play().then(() => setPlaying(true), () => haltRef.current());
    }
  };
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <div
      onClick={() => actions.onDetails(seed)}
      className={cn(
        "group relative h-[360px] w-[320px] cursor-pointer transition-[transform,opacity] duration-[400ms] ease-out",
        !active && "scale-[0.92] opacity-45",
      )}
    >
      <Vinyl seed={seed} out={active} spinning={playing} />
      <article
        className="absolute inset-y-0 left-0 z-10 flex w-[248px] flex-col overflow-hidden rounded-2xl border border-white/[0.06] p-[18px] backdrop-blur-[10px]"
        style={{
          background: "linear-gradient(145deg, rgba(34,34,38,0.86), rgba(14,14,16,0.92))",
          boxShadow: "0 20px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex size-[38px] shrink-0 items-center justify-center rounded-[10px] border border-[rgba(58,158,110,0.3)] bg-[#1a2e22] text-[13px] font-bold text-[#3a9e6e]">
            {initials(seed.who)}
          </div>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-medium text-[#e0e0e0]">{seed.who}</p>
            <p className={cn("mt-px whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.4px]", BADGE_TONE[seed.badge.tone])}>
              {seed.badge.text}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={stop(toggle)}
          disabled={!seed.audioUrl}
          aria-label={playing ? `Pause ${seed.title}` : `Play ${seed.title}`}
          className={cn(
            "mb-2 mt-auto flex size-10 shrink-0 items-center justify-center rounded-full text-[#07140d] transition-colors hover:bg-[#4bcc88] disabled:opacity-30",
            playing ? "bg-[#4bcc88]" : "bg-[#3a9e6e]",
          )}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="size-[18px]">
            {playing ? <path d="M6 4h4v16H6zm8 0h4v16h-4z" /> : <path d="M5 3l14 9-14 9V3z" />}
          </svg>
        </button>
        <h3 className="line-clamp-3 pb-2.5 text-[22px] font-bold leading-[1.12] text-white">{seed.title}</h3>
        <div className="flex flex-wrap gap-1 pb-2.5">
          <Tag className="border-[rgba(58,158,110,0.3)] text-[#4bcc88]">{seed.bpm} BPM</Tag>
          {seed.key && <Tag className="border-[rgba(155,146,240,0.3)] text-[#9b92f0]">{seed.key}</Tag>}
          {seed.genre && <Tag className="border-[rgba(56,189,248,0.3)] text-[#38bdf8]">{seed.genre.toUpperCase()}</Tag>}
        </div>
        <div className="flex gap-1.5 border-t border-white/10 pt-2.5">
          <button
            type="button"
            onClick={stop(() => actions.primary.onClick(seed))}
            className="h-9 flex-1 rounded-md border border-[#3a9e6e] bg-[#3a9e6e] text-[11px] font-bold text-[#07140d] hover:border-[#4bcc88] hover:bg-[#4bcc88]"
          >
            {actions.primary.label}
          </button>
          {actions.onJam && <FootButton onClick={stop(() => actions.onJam?.(seed))}>Jam</FootButton>}
          <FootButton onClick={stop(() => actions.onDetails(seed))}>Details</FootButton>
        </div>
      </article>
    </div>
  );
}

/** The extension's `vinylHtml`: grooves, a conic glint, a tinted label with
 *  genre · tempo · key · maker running round it. */
function Vinyl({ seed, out, spinning }: { seed: HubSeed; out: boolean; spinning: boolean }) {
  const color = labelColor(seed.id);
  const pid = useId();
  const text = labelText({ genre: seed.genre, bpm: seed.bpm, key: seed.key, producer: seed.who });
  return (
    <div
      aria-hidden
      className={cn(
        "absolute right-[-36px] top-1/2 z-0 flex size-[250px] items-center justify-center rounded-full border border-[#333] transition-transform duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]",
        out ? "-translate-y-1/2 translate-x-[26px] rotate-[8deg]" : "-translate-y-1/2 group-hover:translate-x-[26px] group-hover:rotate-[8deg]",
      )}
      style={{
        background: "linear-gradient(135deg, #141414 0%, #000 50%, #141414 100%)",
        boxShadow: "0 12px 34px rgba(0,0,0,0.8), inset 0 0 12px rgba(255,255,255,0.08)",
      }}
    >
      <div
        className="absolute inset-[5px] rounded-full opacity-80 mix-blend-multiply"
        style={{ background: "repeating-radial-gradient(#101010, #101010 2px, #1b1b1b 3px, #101010 4px)" }}
      />
      <div
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background:
            "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.10) 45deg, transparent 90deg, transparent 180deg, rgba(255,255,255,0.10) 225deg, transparent 270deg)",
        }}
      />
      <div
        className="relative z-[2] flex size-[92px] items-center justify-center rounded-full border-2 border-black shadow-[inset_0_0_12px_rgba(0,0,0,0.6)]"
        style={{ background: `${color}22` }}
      >
        <div className="z-[3] size-[11px] rounded-full bg-black" />
        <div
          className="absolute inset-0 opacity-85"
          style={{ color, animation: `spin ${spinning ? 4 : 18}s linear infinite` }}
        >
          <svg viewBox="0 0 100 100" className="size-full">
            <path id={pid} d="M 50,50 m -34,0 a 34,34 0 1,1 68,0 a 34,34 0 1,1 -68,0" fill="transparent" />
            <text fontSize="7.5" fontWeight="bold" letterSpacing="1" fill="currentColor">
              <textPath href={`#${pid}`} startOffset="0%">
                {text}
              </textPath>
            </text>
          </svg>
        </div>
      </div>
    </div>
  );
}

function Tag({ className, children }: { className: string; children: ReactNode }) {
  return <span className={cn("rounded-[3px] border bg-black/50 px-[5px] py-px text-[9px] font-bold tracking-[0.3px]", className)}>{children}</span>;
}

function FootButton({ onClick, children }: { onClick: (e: React.MouseEvent) => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-9 flex-1 rounded-md border border-[#2a2a2c] text-[11px] font-bold text-[#8a8a8e] transition-colors hover:border-[#3a3a3e] hover:text-foreground"
    >
      {children}
    </button>
  );
}

function RoundButton({ label, disabled, onClick, children }: { label: string; disabled: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-10 items-center justify-center rounded-full border border-white/10 hover:bg-white/5 disabled:opacity-30"
    >
      {children}
    </button>
  );
}
